import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { RootState } from 'app/store'
import Alert from 'core/components/Alert'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import QuantitySelector from 'pf9-ui-components/built/components/QuantitySelector'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import AsyncDropdown from 'pf9-ui-components/built/elements/dropdown/AsyncDropdown'
import Radio from 'pf9-ui-components/built/elements/input/Radio'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getGPUProfiles } from '../cluster/deployment/form-components/helpers'
import { updateKaapiMachineDeployment } from '../kaapi/machine-deployment/actions'
import { GPUPartitioningStrategy } from '../kaapi/machines/selectors'
import { listKaapiMachines } from '../kaapi/machines/actions'
import { createConfigMap } from '../resources/config-maps/actions'

interface Props {
  onClose: () => void
  rows: any[]
  clusterName: string
  gpuDetails: Record<string, string>
}

const initialParams = {
  gpuAddons: true,
  partitioningStrategy: GPUPartitioningStrategy.Passthrough,
  migProfile: '',
  timesliceReplica: 1,
  enableAddons: true,
}

const { capi } = ApiClient.getInstance()

const EditGpuConfigurations = ({ onClose, rows, clusterName, gpuDetails }: Props) => {
  const classes = useStyles()
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<{ title: string; message: string } | null>(null)
  const { params, getParamsUpdater } = useParams({
    ...initialParams,
    migProfile: gpuDetails?.migConfig || '',
    partitioningStrategy: gpuDetails?.partitioningStrategy || GPUPartitioningStrategy.Passthrough,
    timesliceReplica: Number(gpuDetails?.replicas || 1),
  })
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant

  // Load Kaapi Machines
  const { reload: reloadKaapiMachines } = useListAction(listKaapiMachines)

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const { gpuProfileOptions } = getGPUProfiles(configMaps, gpuDetails?.model, gpuDetails?.memory)

  // Update Actions
  const {
    update: updateKaapiMachineDeploymentFn,
    updating: updatingKaapiMachineDeployment,
    error: updateKaapiMachineDeploymentError,
  } = useUpdateAction(updateKaapiMachineDeployment)
  const {
    update: createConfigMapFn,
    updating: updatingCreateConfigMap,
    error: createConfigMapError,
  } = useUpdateAction(createConfigMap)

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      if (params.partitioningStrategy === GPUPartitioningStrategy.MIG) {
        // Update each worker nodes from machines
        const { success } = await updateKaapiMachineDeploymentFn({
          name: rows[0]?.metadata?.name,
          customBody: [
            {
              op: 'replace',
              path: '/spec/template/metadata/labels/nvidia.com~1mig.config',
              value: params.migProfile,
            },
          ],
        })

        if (!success) {
          return
        }

        // PATCH Cluster Policy on Workload cluster
        await capi.updateClusterPolicy({
          name: 'cluster-policy',
          body: [
            {
              op: 'replace',
              path: '/spec/migManager/config/name',
              value: 'pf9-mig-config',
            },
            ...(params.migProfile.includes('balanced') // https://platform9.atlassian.net/browse/KAAP-816
              ? [
                  {
                    op: 'replace',
                    path: '/spec/mig/strategy',
                    value: 'mixed',
                  },
                ]
              : []),
          ],
        })
      } else if (params.partitioningStrategy === GPUPartitioningStrategy.Timeslice) {
        // Create new config map on workload cluster
        const { success } = await createConfigMapFn({
          clusterId: clusterName,
          namespace: 'gpu-operator',
          body: {
            metadata: {
              name: 'pf9-time-slicing-config',
              namespace: 'gpu-operator',
            },
            data: {
              any: `version: v1\nflags:\n  migStrategy: none\nsharing:\n  timeSlicing:\n    renameByDefault: false\n    failRequestsGreaterThanOne: false\n    resources:\n      - name: nvidia.com/gpu\n        replicas: ${params.timesliceReplica}`,
            },
          },
        })

        if (!success) {
          return
        }

        // PATCH Cluster Policy on Workload cluster
        await capi.updateClusterPolicy({
          name: 'cluster-policy',
          body: [
            {
              op: 'replace',
              path: '/spec/devicePlugin/config',
              value: { name: 'pf9-time-slicing-config', default: 'any' },
            },
          ],
        })
      }

      // Reload kaapi machines and close modal on success
      reloadKaapiMachines(true)
      onClose()
    } catch (error) {
      setError({
        title: error?.title || 'Error updating GPU configuration',
        message: error?.message || 'Failed to update GPU configuration',
      })
    } finally {
      setSubmitting(false)
    }
  }, [params])

  useEffect(() => {
    if (updateKaapiMachineDeploymentError || createConfigMapError) {
      setError(updateKaapiMachineDeploymentError || createConfigMapError)
    }
  }, [updateKaapiMachineDeploymentError, createConfigMapError])

  const isDefaultStrategy = useMemo(
    () => gpuDetails.partitioningStrategy === GPUPartitioningStrategy.Passthrough,
    [gpuDetails.partitioningStrategy],
  )

  return (
    <ModalForm
      open={true}
      onClose={onClose}
      title="Edit GPU Configuration"
      onSubmit={handleSubmit}
      submitTitle="Save Changes"
      error={error}
      submitting={submitting || updatingKaapiMachineDeployment || updatingCreateConfigMap}
      disableSubmit={submitting || !isDefaultStrategy}
    >
      <div className={classes.container}>
        <div>
          <Alert
            variant={isDefaultStrategy ? 'warning' : 'error'}
            title={isDefaultStrategy ? 'Warning' : ''}
            message={
              isDefaultStrategy
                ? 'The GPU partitioning strategy can be configured only once. Once set, it will apply to all the GPU nodes in the cluster.'
                : 'The GPU partitioning strategy is already configured and can not be updated.'
            }
          />
          <br />
          <Text variant="subtitle2">GPU Partitioning Strategy</Text>
          <Text variant="body2" className={classes.subHeading}>
            Pick a partitioning strategy to match your workload’s performance and isolation needs.
            The partitioning strategy defines how GPU resources are allocated across the cluster.
          </Text>
          <div className={classes.radioContainer}>
            <div
              className={classes.radioItem}
              onClick={() => {
                getParamsUpdater('partitioningStrategy')(GPUPartitioningStrategy.Passthrough)
              }}
            >
              <Radio
                label="Passthrough"
                id="passthrough"
                checked={params?.partitioningStrategy === GPUPartitioningStrategy.Passthrough}
              />
              <HintTooltip
                message="Grants exclusive access to a physical GPU for highest isolation and performance."
                align={{ vertical: 'top', horizontal: 'middle' }}
              />
            </div>
            <div
              className={classes.radioItem}
              onClick={() => getParamsUpdater('partitioningStrategy')(GPUPartitioningStrategy.MIG)}
            >
              <Radio
                label="MIG"
                id="mig"
                checked={params?.partitioningStrategy === GPUPartitioningStrategy.MIG}
              />
              <HintTooltip
                className={classes.hint}
                message="MIG (Multi-Instance GPU) splits supported NVIDIA cards into physically isolated slices, letting multiple pods run concurrently with hard resource guarantees."
                align={{ vertical: 'top', horizontal: 'middle' }}
              />
            </div>
            <div
              className={classes.radioItem}
              onClick={() =>
                getParamsUpdater('partitioningStrategy')(GPUPartitioningStrategy.Timeslice)
              }
            >
              <Radio
                label="Timeslice"
                id="timeslice"
                checked={params?.partitioningStrategy === GPUPartitioningStrategy.Timeslice}
              />
              <HintTooltip
                className={classes.hint}
                message="Time Slice shares the same GPU across pods on a schedule."
                align={{ vertical: 'top', horizontal: 'middle' }}
              />
            </div>
          </div>
        </div>
        {/* Show Profile Dropdown and Quantity Selector for MIG */}
        {params?.partitioningStrategy === GPUPartitioningStrategy.MIG && (
          <>
            <div className={classes.profileContainer}>
              <DropdownField
                label="MIG Profile"
                id="mig-profile"
                value={params?.migProfile}
                items={gpuProfileOptions}
                onChange={getParamsUpdater('migProfile')}
                DropdownComponent={AsyncDropdown}
                loading={loadingConfigMaps}
                disabled={!isDefaultStrategy}
                required
              />
              {/* {params?.migProfile && (
                <div className={classes.profileDetails}>
                  <Text variant="body2">{gpuSizes[params?.migProfile]?.key}</Text>
                  <QuantitySelector
                    id="quantity"
                    onChange={noop}
                    value={gpuSizes[params?.migProfile]?.value}
                    min={1}
                    disabled
                  />
                </div>
              )} */}
            </div>
            {/* Show Error if no MIG profiles found */}
            {!loadingConfigMaps && gpuProfileOptions.length === 0 && (
              <ErrorMessage>No MIG profiles found. Please check your GPU Model.</ErrorMessage>
            )}
          </>
        )}
        {/* Show Timeslice Replicas for Timeslice */}
        {params?.partitioningStrategy === GPUPartitioningStrategy.Timeslice && (
          <div className={classes.timesliceReplicas}>
            <Text variant="body2" component="div" className={classes.timesliceReplicasText}>
              Replicas per Physical GPU
              <HintTooltip
                className={classes.hint}
                message="Sets how many virtual GPUs are created from a physical GPU for Time Slice sharing. The higher the replica count, the smaller each pod’s time slice."
              />
            </Text>
            <QuantitySelector
              id="quantity"
              onChange={getParamsUpdater('timesliceReplica')}
              value={params?.timesliceReplica}
              disabled={!isDefaultStrategy}
            />
          </div>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: theme.spacing(2),
  },
  profileContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  profileDetails: {
    display: 'grid',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    paddingLeft: theme.spacing(3),
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  timesliceReplicas: {
    display: 'grid',
    gap: theme.spacing(2),
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  subHeading: {
    marginTop: theme.spacing(2),
  },
  radioContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  radioItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  hint: {
    cursor: 'pointer',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'pointer',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
    },
  },
  timesliceReplicasText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

export default EditGpuConfigurations
